import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import dayjs from "dayjs";
import { STATUS, TYPE } from "../../design/ui";
import {
  statusKey,
  prioKey,
  typeKey,
  fmtDate,
  fmtDateTime,
  fmtInputDate,
  fmtRelative,
  STATUS_CODE,
  PRIO_LABEL,
  LABEL_TO_STATUS,
  REVIEW_TYPE_BY_CODE,
  REVIEW_TYPE_CODE,
  REVIEW_STATUS_LABEL,
  reviewStatusKey,
  reviewTypeLabel,
  reviewOutcome,
  toPlainText,
  imagesOf,
  mentionHtml,
} from "../../design/domain";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPostForm, apiPutForm, apiPostJson, apiPutJson, apiDelete, currentAccount } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { hasPermission } from "../../utils/permission";
import { jwtEncode } from "../../utils/jwt";
import request from "../../utils/request";
import { getToken } from "../../utils/token";
import TaskGlance from "./TaskGlance";
import TaskWork from "./TaskWork";
import TaskReference from "./TaskReference";
import Button from "@mui/material/Button";
import { Banner } from "../mui";
import { PageLoading } from "../loading";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { MdCheck, MdCircle, MdErrorOutline, MdFactCheck, MdRemove, MdWarningAmber } from "react-icons/md";

/**
 * 工項詳情 — 資料層與組裝（畫面搬到 el-pm-template_4.html:625-757 的三段式）
 *
 * 這支元件現在**只負責取資料、算出畫面要用的形狀、把動作接起來**，
 * 渲染分給三個子元件：
 *   · TaskGlance     一眼區（型別／提醒／標題／誰負責什麼時候交／進度／兩顆動作）
 *   · TaskWork       工作區（要做什麼、我的回報）
 *   · TaskReference  參考區（五個收合區 ＋ 收「設計稿沒有位置」那幾塊的第六個）
 *
 * 為什麼要拆：原本是 2,647 行的單檔，15 個區塊平鋪在同一頁，改一個地方要先讀完全部。
 * 拆檔之後這裡剩下資料流，版面問題只會發生在三個子元件裡。
 *
 * **對外的 props 一個都沒有動**（taskId / projectName / onBack / onChanged / onEdit /
 * onOpenTask / readOnly / embedded / reviewMeta / taskCode）。三個呼叫端
 * （pages/Project/Detail.js、components/task/TaskDrawer.js 給 /Board 與 /MyTask 用）
 * 不在這次的改動範圍內，所以 embedded / readOnly 的語意維持原樣，只是改成往下傳。
 *
 * 舊有的 /Task 系列端點走「PayloadHelper 加密信封」（?Data=<jwt>），
 * 新端點（/Review /Workflow /Notification /MyTask）走純 query / JSON。
 * 為了讓本元件同時相容改造中與改造後的後端，送出時「信封與扁平欄位一起帶」——
 * 兩種 model binding 都只會挑自己認得的欄位，多餘的會被忽略。
 */

/* ---------- 常數（設計稿字面值） ---------- */

/**
 * 快速回報的狀態下拉選項（設計稿 L1223 qrStatusOpts）。
 *
 * 不含「已完成」—— 這一區是「回報」，把自己的工作宣告成完成不該只是改一個下拉。
 * 正常流程裡也不需要誰去改：把關者核准的那一刻，ReviewService.Decide 就自動把工項
 * 推到已完成。「⋯ 更多」裡的轉換鈕（依 /Workflow/Transitions 回來的 allowed 判斷）
 * 只剩「不送審、直接結案」與「審核通過卻沒自動完成」時的補標（見 transitions 的 stuckDone）。
 *
 * 「完成前一定有人審過」是後端在保證的，而且保證得比工作流資料還硬：
 * WorkflowService.EvaluateDoneGuard（api/Web/Services/WorkflowService.cs:169）對任何
 * legacy_status = 3（已完成）的目標狀態一律要求「需先送審並經把關者核准才能標記完成」，
 * 不管那條轉換上有沒有掛 review_approved validator —— 註解寫得很清楚：
 * validator 是各環境不一定同步的資料，而「完成必須經過審核」是產品規則，寫在程式裡。
 * 驗收條件（all_ac_checked）反過來是資料驅動的：只有掛了那條 validator 的轉換才會擋。
 * GetAvailableTransitions（算 allowed）與 DoTransition（真的執行）跑的是同一組守門，
 * 所以這裡亮著的鈕按下去不會突然失敗。
 */
const QR_STATUS_OPTS = ["（不變更）", "待處理", "進行中", "審核中", "已關閉"];

/**
 * 附件副檔名配色 — 舊版檔案圖示用的。
 *
 * **暫不使用**：新版面沒有「檔案型別色塊」這個語彙（bundle.css 也沒有），
 * 而這些是寫死的 hex，深色主題下不會跟著換。附件改成一行文字 ＋ 下載／刪掉兩顆按鈕。
 * 依「不准刪」的規矩把常數留在檔案裡，將來若要恢復圖示直接取用。
 */
// eslint-disable-next-line no-unused-vars
const ATT_ICON = {
  PDF: { bg: "rgba(217,45,32,.12)", c: "#d92d20" },
  FIG: { bg: "rgba(124,92,255,.14)", c: "#7c5cff" },
  DOC: { bg: "rgba(37,99,235,.12)", c: "#2563eb" },
  DOCX: { bg: "rgba(37,99,235,.12)", c: "#2563eb" },
  XLS: { bg: "rgba(14,124,102,.12)", c: "#0e7c66" },
  XLSX: { bg: "rgba(14,124,102,.12)", c: "#0e7c66" },
  PNG: { bg: "rgba(8,145,178,.12)", c: "#0891b2" },
  JPG: { bg: "rgba(8,145,178,.12)", c: "#0891b2" },
  ZIP: { bg: "rgba(220,104,3,.12)", c: "#dc6803" },
};

/** 狀態 → StatusChip 的 kind 與前綴符號（與 pages/MyTask/Index.js 的 STATUS_TAG 同一套配色） */
const STATUS_TAG = {
  pending: { kind: "closed", mark: <MdRemove /> },
  progress: { kind: "progress", mark: <MdCircle size="0.6em" /> },
  review: { kind: "warn", mark: <MdErrorOutline /> },
  done: { kind: "done", mark: <MdCheck /> },
  closed: { kind: "closed", mark: <MdRemove /> },
};

/** 後端有沒有真的回型別；認不得時 typeKey() 一律退回 task，無條件貼徽章會全部標成「工作」 */
const TYPE_FIELD_KEYS = ["issue_type_key", "issueTypeKey", "issue_type", "IssueTypeKey", "type"];

/**
 * 轉換鈕的白話名。
 *
 * 設計稿把「送審」寫成「送出給審核人」、把「直接完成」寫成「不送審、直接結案」——
 * 使用者要在按下去之前就知道這一顆會發生什麼事。工作流回來的名稱若不在表裡就原樣顯示，
 * 不自己替別人設定的流程改名。
 */
const TRANS_LABEL = {
  送審: "送出給審核人",
  直接完成: "不送審、直接結案",
  關閉: "不送審、直接結案",
  開始: "開始",
  退回: "退回修改",
  重啟: "重新開啟",
  核准完成: "核准完成",
};

/* ---------- 小工具 ---------- */

/** 任一 API 失敗都不能讓整頁掛掉——取不到就回 fallback */
/** 進度回報讀取失敗的記號（與「讀到了、但一筆都沒有」的空陣列分開） */
const RECORDS_FAILED = Object.freeze({ failed: true });

async function safe(fn, fallback) {
  try {
    const r = await fn();
    return r === undefined || r === null ? fallback : r;
  } catch (e) {
    return fallback;
  }
}

/** 位元組 → 1.2 MB */
function fmtSize(n) {
  const b = Number(n) || 0;
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}

/**
 * 進度回報上夾帶的檔案（task_progress 的 file_*，也就是 POST /MyTask/Progress 帶的 file）。
 *
 * 跟工項附件（/Task/Attachment）是兩回事：檔案掛在「那一筆回報」上，
 * 下載走 /MyTask/Progress/Download/{回報 id}，所以回報的 id 一定要留住。
 * has_file 是後端依 file_path 算的；沒有這個欄位時退回看有沒有檔名
 * （與 pages/MyTask/Index.js 的判斷同一套）。沒有檔案回 null，呼叫端展開後什麼都不會多。
 */
function progressFileOf(r) {
  const fileName = _.get(r, "file_name") || "";
  const hasFile = !!(_.get(r, "has_file") ?? fileName);
  if (!hasFile || _.get(r, "id") == null) return null;
  const fileSize = Number(_.get(r, "file_size")) || 0;
  return {
    hasFile: true,
    recordId: r.id,
    fileName,
    fileSize,
    // 拿不到大小就不寫，不要顯示成「0 B」讓人以為是空檔案
    fileSizeText: fileSize > 0 ? fmtSize(fileSize) : "",
  };
}

/**
 * 把取回來的 blob 存成檔案。下載端點都有 [Authorize]，直接開 <a href> 不會帶
 * Bearer token（必定 401），所以一律先取 blob、再用暫時的 object URL 觸發存檔。
 */
function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 下載失敗要講得出原因。
 *
 * 不能直接丟給 errMsg：responseType 是 blob 時，錯誤回應的 body 也是一個 Blob，
 * apiErrorMessage 讀不到裡面的 res_msg，只會退回「Request failed with status code 400」
 * 這種給工程師看的字。所以先把 Blob 讀成文字，拿出後端寫的原因
 * （例如「下載失敗: 檔案不存在」）。
 *
 * 讀不出原因才依狀態碼補一句。404 不能沿用全站那句「可能已被其他人刪除」——
 * 沒有權限看這件事的人拿到的也是「找不到」（後端不透露檔案存不存在），兩種情況都要講到。
 */
async function downloadErrMsg(e) {
  const data = _.get(e, "response.data");
  if (data && typeof data.text === "function") {
    try {
      const msg = _.get(JSON.parse(await data.text()), "res_msg");
      if (msg) return String(msg);
    } catch (ignore) {
      // body 不是 JSON（例如閘道器回的 HTML 錯誤頁）→ 交給下面依狀態碼說明
    }
  }
  const status = _.get(e, "response.status");
  if (status === 404) return "找不到此檔案：可能已被刪除，或您沒有檢視此工項的權限。";
  // 401／403／413／5xx 與連不上，全站那支已經講得很清楚
  if (!status || [401, 403, 413].includes(status) || status >= 500) return errMsg(e, "無法下載此檔案");
  return `無法下載此檔案（伺服器回應 ${status}），請重新整理頁面後再試。`;
}

/** 附件 meta 的 6/2 短日期 */
function fmtMD(v) {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("M/D") : "";
}

/**
 * 「9 月 18 日」。日期一律走 dayjs（已載 zh-tw），用當地時區的日界；
 * 跨年才補年份，免得今年的日期一路寫成 2026 年。
 */
function fmtDay(v) {
  if (!v) return "";
  const d = dayjs(v);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M 月 D 日") : d.format("YYYY 年 M 月 D 日");
}

/** CI 狀態（後端可能給字串或數字）→ 白話文案 */
function ciText(v) {
  const s = typeof v === "number" ? ["run", "pass", "fail"][v] : String(v || "run");
  if (s === "pass" || s === "success") return "通過";
  if (s === "fail" || s === "failure") return "失敗";
  return "執行中";
}

/** 測試結果 → 白話文案 */
function testText(v) {
  const s = typeof v === "number" ? ["run", "pass", "fail"][v] : String(v || "run");
  if (s === "pass") return "通過";
  if (s === "fail") return "失敗";
  return "執行中";
}

/** 審核者決議：0 未決 / 1 通過 / 2 要求修改（沿用後端 ReqReviewDecision） */
const DECISION_LABEL = { 0: "待決", 1: "通過", 2: "要求修改" };

/** 錯誤訊息（後端統一放在 res_msg；403 之類沒有 body 的由 apiErrorMessage 補上說明） */
function errMsg(e, fallback) {
  return apiErrorMessage(e, fallback);
}

function TaskDetailPanel({
  taskId,
  projectName,
  onBack,
  onChanged,
  onEdit,
  onOpenTask,
  readOnly,
  embedded,
  reviewMeta,
  taskCode,
}) {
  const navigate = useNavigate();
  const { toast, confirmDelete, setDetailView } = useApp();

  /* ---------- 遠端資料 ---------- */
  const [raw, setRaw] = useState(null); // /Task 原始物件
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [reviews, setReviews] = useState([]);
  /**
   * 後端講的「這一輪算不算審核通過」（/Review/List 的 approval_effective）。
   * 最新一張通過、但工項之後被重新打開過的，那張通過不算數；只看 reviews[0] 分不出來。
   * null = 後端沒給（舊版 API），退回只看最新一張。
   */
  const [approvalEffective, setApprovalEffective] = useState(null);
  /** 把關者改派紀錄（/Review/List 的 reassigns）；時間軸靠它講出改派經過 */
  const [reassigns, setReassigns] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [wf, setWf] = useState(null); // /Workflow/Transitions
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  /* 解決結論（關閉工項時要選）＋ 正在等結論的那個轉換 */
  const [resolutions, setResolutions] = useState([]);
  const [pendingTrans, setPendingTrans] = useState(null);
  const [pendingResId, setPendingResId] = useState("");
  const [links, setLinks] = useState(null); // GET /Task/Links（只有讀，沒有建立端點）
  const [devLinks, setDevLinks] = useState([]); // GET /Task/DevLinks（只有讀，沒有建立端點）
  const [tests, setTests] = useState([]); // GET /Task/Tests（只有讀，沒有建立端點）
  const [watch, setWatch] = useState({ watching: false, watchers: [] });
  const [records, setRecords] = useState([]); // 進度回報歷程
  /**
   * 進度回報讀不到（GET /MyTask/Progress/{id} 失敗）。/Task 看得到的人不一定讀得到回報
   * （後端依資料範圍擋，擋下來回「任務不存在」），吞成空陣列的話「以前的紀錄」會少掉
   * 所有回報與檔案，看起來像從來沒人回報過 —— 要講清楚是「讀不到」，不是「沒有」。
   */
  const [recordsFailed, setRecordsFailed] = useState(false);

  /* ---------- 本地互動狀態 ---------- */
  const [desc, setDesc] = useState("");
  const [sol, setSol] = useState("");
  const [dodDraft, setDodDraft] = useState("");
  const [subs, setSubs] = useState([]);
  const [subDraft, setSubDraft] = useState("");
  const [commentDraft, setCommentDraft] = useState("");
  const [reviewType, setReviewType] = useState(REVIEW_TYPE_BY_CODE[0]);
  const [pickedReviewers, setPickedReviewers] = useState([]);
  const [resubmitting, setResubmitting] = useState(false);
  const [qrStatus, setQrStatus] = useState(QR_STATUS_OPTS[0]);
  const [qrPct, setQrPct] = useState("");
  const [qrHours, setQrHours] = useState("");
  const [qrNote, setQrNote] = useState("");
  /** 代負責人回報的進度（誰看得到這一塊見下方的 canProxy） */
  const [proxyPct, setProxyPct] = useState("");
  const [busy, setBusy] = useState(false);

  // 存檔時要讀「最新的」原始物件，避免 stale closure 覆蓋掉其他欄位
  const rawRef = useRef(null);
  /** 隱藏的檔案挑選器（附件上傳；multiple，一次可選多個） */
  const attFileRef = useRef(null);
  rawRef.current = raw;

  /* ---------- 使用導覽：讓 Layout 知道目前在詳情檢視 ---------- */
  useEffect(() => {
    // 內嵌在抽屜裡時，頁面本身仍停在清單，改動全域導覽狀態會讓返回鍵／麵包屑錯亂
    if (embedded) return undefined;
    setDetailView("detail");
    return () => setDetailView("list");
  }, [setDetailView, embedded]);

  /* ---------- 載入 ---------- */
  const loadTask = useCallback(async () => {
    if (!taskId) return null;
    const env = { Data: jwtEncode({ TaskId: Number(taskId) }), id: taskId, taskId };
    const t = await safe(() => apiGet("/Task", env), null);
    if (t) {
      setRaw(t);
      setDesc(t.description || "");
      setSol(t.solution || t.resolution_note || "");
      setSubs(
        (t.sub_tasks || []).map((x) => ({
          id: x.id,
          text: x.title,
          status: Number(x.status) || 0,
          done: Number(x.status) === 3 || Number(x.status) === 4,
        }))
      );
    }
    return t;
  }, [taskId]);

  const loadSide = useCallback(
    async (t) => {
      if (!taskId) return;
      const pid = _.get(t, "project_id");
      const [rv, cm, at, wfd, lk, dv, ts, wt, rc, mb, rs] = await Promise.all([
        safe(() => apiGet("/Review/List", { taskId }), {}),
        safe(
          () =>
            apiGet("/Task/Comment/List", {
              Data: jwtEncode({ TaskId: Number(taskId) }),
              taskId,
            }),
          []
        ),
        safe(() => apiGet(`/Task/Attachment/List/${taskId}`), []),
        safe(() => apiGet("/Workflow/Transitions", { taskId }), null),
        safe(() => apiGet("/Task/Links", { taskId }), null),
        safe(() => apiGet("/Task/DevLinks", { taskId }), []),
        safe(() => apiGet("/Task/Tests", { taskId }), []),
        safe(
          () => apiGet("/Notification/Watch", { entityType: "task", entityId: taskId }),
          { watching: false, watchers: [] }
        ),
        safe(() => apiGet(`/MyTask/Progress/${taskId}`), RECORDS_FAILED),
        pid
          ? safe(
              () =>
                apiGet("/Project/Member/List", {
                  Data: jwtEncode({ ProjectId: Number(pid) }),
                  projectId: pid,
                }),
              []
            )
          : Promise.resolve([]),
        // 解決結論：關閉工項時要選一個，先撈起來備用
        safe(() => apiGet("/Workflow/Resolutions"), []),
      ]);
      setResolutions(Array.isArray(rs) ? rs : []);
      setReviews(_.get(rv, "reviews", []) || []);
      setApprovalEffective(_.get(rv, "approval_effective", null));
      setReassigns(_.get(rv, "reassigns", []) || []);
      setCriteria(_.get(rv, "criteria", []) || []);
      setComments(Array.isArray(cm) ? cm : _.get(cm, "data_list", []) || []);
      setAttachments(Array.isArray(at) ? at : []);
      setWf(wfd);
      setLinks(lk);
      setDevLinks(Array.isArray(dv) ? dv : _.get(dv, "data_list", []) || []);
      setTests(Array.isArray(ts) ? ts : _.get(ts, "data_list", []) || []);
      setWatch({
        watching: !!_.get(wt, "watching", false),
        watchers: _.get(wt, "watchers", []) || [],
      });
      setRecordsFailed(rc === RECORDS_FAILED);
      setRecords(
        rc === RECORDS_FAILED ? [] : Array.isArray(rc) ? rc : _.get(rc, "data_list", []) || []
      );
      setMembers(Array.isArray(mb) ? mb : []);
      // 審核決定送出後要拿「重讀回來的」審核單判斷結果（見 decide），state 要等下一輪 render 才看得到
      return { reviews: _.get(rv, "reviews", []) || [] };
    },
    [taskId]
  );

  /** 回傳剛讀到的工項與審核單；只有 decide 用得到，其餘呼叫端照舊不理它 */
  const reload = useCallback(async () => {
    const t = await loadTask();
    const side = await loadSide(t);
    return { task: t, reviews: _.get(side, "reviews", []) || [] };
  }, [loadTask, loadSide]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const t = await loadTask();
      if (!alive) return;
      await loadSide(t);
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [loadTask, loadSide]);

  /* ---------- 衍生資料 ---------- */

  const nameOf = useCallback(
    (account) => {
      const m = members.find((x) => x.account === account);
      return _.get(m, "name") || account || "";
    },
    [members]
  );

  const statusK = statusKey(_.get(raw, "status"));
  const typeField = TYPE_FIELD_KEYS.find((f) => _.get(raw, f));
  const typeK = typeKey(_.get(raw, typeField));
  const prioK = prioKey(_.get(raw, "priority"));
  const st = STATUS[statusK] || STATUS.pending;
  // /Task 目前沒有回 issue_key，呼叫端（清單頁）手上通常已經有代碼，優先採用它
  const code = _.get(raw, "issue_key") || taskCode || (raw ? `#${raw.id}` : "");
  const title = _.get(raw, "title", "");
  const assigneeName = _.get(raw, "assignee_name") || _.get(raw, "assignee") || "";
  const assigneeAccount = _.get(raw, "assignee") || "";
  const score = _.get(raw, "score", null);
  const est = Number(_.get(raw, "estimated_hours") || 0);
  const act = Number(_.get(raw, "actual_hours") || 0);
  const projName = projectName || _.get(raw, "project_name") || "";
  const dueRaw = _.get(raw, "due_date");
  const progressPct = Math.max(0, Math.min(100, Number(_.get(raw, "progress") || 0)));

  const labels = useMemo(() => {
    const t = _.get(raw, "tags");
    if (!t) return [];
    if (Array.isArray(t)) return t;
    return String(t)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }, [raw]);

  // rollup 子項進度：後端尚未回 rollup_* 欄位，先用子任務完成比例代替
  const prog = useMemo(() => {
    const total = Number(_.get(raw, "rollup_total_count") || 0) || subs.length;
    if (!total) return null;
    const done = Number(_.get(raw, "rollup_done_count") || 0) || subs.filter((s) => s.done).length;
    const pct = Number(_.get(raw, "progress") || 0) || Math.round((done / total) * 100);
    return { done, total, pct };
  }, [raw, subs]);

  const dodDone = criteria.filter((c) => c.is_checked).length;
  const allDod = criteria.length > 0 && dodDone === criteria.length;

  // 最新一筆審核決定畫面狀態（設計稿的 reviewStatus：none / pending / approved / rejected）
  const latestReview = reviews[0] || null;
  /*
   * 最新一張通過、後端卻說這一輪不算通過：通過之後工項被重新打開過，重做的部分沒人審。
   * 這時候畫面要當成「還沒送審」，讓他看得到送審表單，而不是「審核已經通過了」和補標鈕。
   */
  const staleApproval =
    !!latestReview && reviewStatusKey(latestReview.status) === "approved" && approvalEffective === false;
  const reviewStatus = useMemo(() => {
    if (resubmitting || !latestReview || staleApproval) return "none";
    const k = reviewStatusKey(latestReview.status);
    return k === "returned" ? "rejected" : k;
  }, [latestReview, resubmitting, staleApproval]);
  const reviewed = !!latestReview && reviewStatusKey(latestReview.status) === "approved" && !staleApproval;

  /* ---------- 工作流守門 ---------- */

  /**
   * 轉換清單：優先用後端工作流（含真正的守門原因）；
   * 工作流尚未設定 / 取不到時，退回設計稿 gates() 的規則，畫面才不會空掉。
   *
   * confirmClose：這一步會讓工項不經審核就結束（送審沒發生過、審核紀錄是空的），
   * 一眼區按下去要先跳確認視窗。
   */
  const transitions = useMemo(() => {
    /*
     * 「審核中 → 已完成」這一顆在正常流程裡已經沒有用了。
     *
     * 審核通過的瞬間，後端 ReviewService.Decide 就自己把工項推到「已完成」；
     * 審核通過之前按它，又一定被 WorkflowService.EvaluateDoneGuard 擋下
     * （「需先送審並經把關者核准才能標記完成」）。留一顆永遠鎖著、還寫著「要等核准」的鈕，
     * 等於告訴使用者「核准之後要回來按這顆」—— 那正是這次要拿掉的誤會。
     *
     * 只留一種情況：最新一張審核單已經通過、工項卻還停在審核中。那是自動完成上線之前
     * 就核准的舊資料，或自動轉換被別的守門擋下（例如還有驗收條件沒勾），
     * 這時候它是唯一的出口，名字也改成講清楚「審核已經過了，這顆只是補標」。
     * 鎖住的話原因照常顯示（多半是驗收條件），那句話就是他該去做的事。
     *
     * 不動的：進行中的「不送審、直接結案」（另一條路，confirmClose 會先跳確認）、
     * 審核中的「退回」、已完成的「重新打開」。
     */
    const stuckDone = (t) => statusK === "review" && t.target === "done";
    const mark = (t) => ({
      ...t,
      label: stuckDone(t) ? "審核已通過，標記為已完成" : TRANS_LABEL[t.label] || t.label,
      confirmClose: (t.target === "done" || t.target === "closed") && !reviewed,
    });
    const api = _.get(wf, "transitions", []) || [];
    if (api.length) {
      return api
        /*
         * 不再把「關閉」放進正常流程：已完成就是終點。
         *
         * 已關閉在這套系統裡什麼都沒買到 —— 所有統計（里程碑狀態、專案進度、
         * 吞吐量、逾期判定…）一律把它與已完成當同一件事，而它要求填的解決結論
         * 從頭到尾沒有任何地方讀出來。留著只是逼使用者多按一次、多填一個沒人看的欄位。
         *
         * 只擋「從已完成再往關閉」這一步；工項若已經是已關閉（舊資料），
         * 通往其他狀態的轉換照常顯示，才拉得回來。
         */
        .filter((t) => {
          const legacy = _.get(t, "to_status.legacy_status");
          if (legacy === 4 && statusK === "done") return false;
          // 審核中 → 已完成：審核還沒通過就不畫（理由見上方 stuckDone）
          if (legacy === 3 && statusK === "review" && !reviewed) return false;
          return true;
        })
        .map((t) => {
          const locked = !t.allowed;
          const legacy = _.get(t, "to_status.legacy_status");
          return mark({
            key: `wf-${t.id}`,
            label: t.name,
            locked,
            reason: locked ? (t.blocked_reasons || []).join("；") : "",
            transitionId: t.id,
            // 有些轉換（例如「關閉」）後端要求一併帶上解決結論，
            // 少了它會被擋下並回「需選擇解決結論」。這個旗標必須傳下去，
            // 否則按鈕永遠是死路 —— 按下去必定失敗
            needsResolution: !!t.requires_resolution,
            target: legacy == null ? null : ["pending", "progress", "review", "done", "closed"][legacy],
          });
        });
    }
    const btn = (label, target, locked, reason) =>
      mark({
        key: `local-${target}-${label}`,
        label,
        target,
        locked,
        reason: locked ? reason : "",
        transitionId: null,
      });
    if (statusK === "pending") return [btn("開始", "progress", false, "")];
    if (statusK === "progress")
      return [
        btn("送審", "review", false, ""),
        /*
         * 「直接完成」比照「核准完成」，一樣要有通過的審核才解鎖。
         * 原本只看 allDod —— 驗收條件自己勾一勾就能完成，繞過整個把關流程。
         * 後端 WorkflowService.EvaluateDoneGuard 已經擋住了，這裡跟著鎖，
         * 才不會給一顆按了必定失敗的按鈕。
         */
        btn(
          "直接完成",
          "done",
          !(reviewed && allDod),
          reviewed ? "尚有未完成的驗收條件，完成後才能結案。" : "須先送出給審核人並通過審核，才能結案。"
        ),
      ];
    /*
     * 審核中只留「退回」。「核准完成」只在審核已經通過、工項卻沒跟著完成時才出現
     * （理由見上方 stuckDone）—— 審核還沒通過時，通過那一刻就會自動完成，不必再按。
     */
    if (statusK === "review")
      return [
        ...(reviewed
          ? [btn("核准完成", "done", !allDod, "尚有未完成的驗收條件，完成後才能結案。")]
          : []),
        btn("退回", "progress", false, ""),
      ];
    // 已完成是終點，只留「重啟」讓做錯的可以拉回來（不再提供關閉，理由見上方 filter）
    if (statusK === "done") return [btn("重啟", "progress", false, "")];
    return [btn("重啟", "progress", false, "")];
  }, [wf, statusK, allDod, reviewed]);

  /* ---------- 活動紀錄 ---------- */

  /**
   * 後端沒有「單一工項的活動紀錄」端點，時間軸由三處拼出來：
   * ① 設計稿 seedLogs() 依工項本身狀態推導的骨架 ② 真實審核事件 ③ 真實進度回報。
   *
   * 骨架與真實事件講的是同一件事時，只留真實的那一筆。以前「已完成」的骨架會自己推一筆
   * 「系統：審核通過」＋「狀態改成已完成」，審核單上又有一筆「某某：審核通過」——
   * 同一次審核通過出現兩次，其中一次連時間都是拿到期日充數的。
   * 審核通過現在會自動完成，這兩件事是同一刻發生的，併成審核單上的那一行（見下面的 closer）。
   */
  const logs = useMemo(() => {
    if (!raw) return [];
    const creator = nameOf(_.get(raw, "create_id")) || _.get(raw, "create_id") || "";
    const created = fmtDateTime(_.get(raw, "create_at"));
    const startT = fmtDate(_.get(raw, "start_date"));
    const dueT = fmtDate(_.get(raw, "due_date"));
    const scoreText = score == null ? "" : `，得分 ${score}`;
    const latest = reviews[0] || null; // 後端由新到舊給
    const latestKey = latest ? reviewStatusKey(latest.status) : null;
    /*
     * 「審核通過」與「變成已完成」是不是同一刻：自動完成是在同一個請求裡做的，
     * 審核單的 closed_at 與工項的 completed_at 只差幾毫秒；差了一分鐘以上，
     * 就是自動完成上線前的舊資料 —— 當時是有人事後再按了一次「完成」，那是兩件事，照實分兩行。
     */
    const completedAt = _.get(raw, "completed_at");
    const autoDone =
      statusK === "done" &&
      latestKey === "approved" &&
      !!completedAt &&
      !!latest.closed_at &&
      Math.abs(dayjs(completedAt).valueOf() - dayjs(latest.closed_at).valueOf()) <= 60000;
    const out = [
      { type: "create", actor: creator, text: "建立工項", time: created },
      { type: "assign", actor: creator, text: `指派給 ${assigneeName}`, time: created },
    ];
    if (["progress", "review", "done", "closed"].includes(statusK)) {
      out.push({
        type: "progress",
        actor: assigneeName,
        text: "開始處理，狀態變更為「進行中」",
        time: startT,
      });
    }
    // 有審核單時，下面會照審核單寫「送出審核，請 X 看」，骨架不再推一筆時間是到期日的假紀錄
    if (statusK === "review" && !reviews.length) {
      out.push({
        type: "review",
        actor: assigneeName,
        text: "送出審核，等待審核人審核",
        time: dueT,
      });
    }
    if (statusK === "closed") {
      out.push({ type: "closed", actor: assigneeName, text: "結案", time: dueT });
    }
    reviews
      .slice()
      .reverse()
      .forEach((r) => {
        const moved = reassigns.filter((x) => Number(x.review_id) === Number(r.id));
        out.push({
          type: "review",
          actor: nameOf(r.opened_by),
          // 改派過的審核單上掛的是「現在」的把關者，不能算在發起人頭上；改派經過另外列在下面
          text: moved.length
            ? `送出審核（${reviewTypeLabel(r.review_type)}）`
            : `送出審核（${reviewTypeLabel(r.review_type)}），審核人：${(r.assignees || [])
                .map((a) => nameOf(a.reviewer_id))
                .join("、")}`,
          time: fmtRelative(r.opened_at),
        });
        moved.forEach((x) => {
          const from = String(x.from || "")
            .split(",")
            .filter(Boolean)
            .map((acc) => nameOf(acc))
            .join("、");
          out.push({
            type: "assign",
            actor: nameOf(x.by),
            text: from ? `審核人由 ${from} 改為 ${nameOf(x.to)}` : `指定 ${nameOf(x.to)} 為審核人`,
            time: fmtRelative(x.at),
          });
        });
        /*
         * 每個把關者一行。讓審核單結案的那一票（通過：最後一個通過的人；要求修改：第一個
         * 要求修改的人）順便講工項跟著變成什麼 —— 一次審核通過只出現這一行，不再另外推一筆。
         *
         * 「跟著變成什麼」只在確定的時候寫：
         *   · 通過 → 已完成：只有最新這一張、而且 autoDone 成立（同一刻完成）才寫；
         *   · 要求修改 → 進行中：只有最新這一張、而且工項現在真的是進行中才寫。
         * 舊的輪次查不到當時工項怎麼變（沒有單一工項的狀態歷程端點），就只寫審核本身。
         */
        const isLatest = !!latest && String(r.id) === String(latest.id);
        const rk = reviewStatusKey(r.status);
        const decided = (r.assignees || []).filter((a) => Number(a.decision) > 0);
        const byTime = (a) => (a.decided_at ? dayjs(a.decided_at).valueOf() || 0 : 0);
        let closer = null;
        if (rk === "approved") closer = _.maxBy(decided.filter((a) => Number(a.decision) === 1), byTime) || null;
        if (rk === "rejected") closer = _.minBy(decided.filter((a) => Number(a.decision) === 2), byTime) || null;
        decided.forEach((a) => {
          const pass = Number(a.decision) === 1;
          let text = pass ? "審核通過" : "要求修改";
          if (a === closer && isLatest && pass && autoDone) {
            text = `審核通過，工項自動變更為「已完成」${scoreText}`;
          }
          if (a === closer && isLatest && !pass && statusK === "progress") {
            text = "要求修改，狀態退回「進行中」";
          }
          out.push({
            type: pass ? "done" : "closed",
            actor: nameOf(a.reviewer_id),
            text,
            time: fmtRelative(a.decided_at),
          });
        });
      });
    /*
     * 已完成：自動完成的那一次已經併在審核通過那一行，這裡不寫。
     * 其餘情況（舊資料是事後有人再按了一次完成、或審核單讀不到）只寫「變成已完成」這件確定
     * 發生的事 —— 沒有審核單可以對照，就不替它編一筆「審核通過」，也不編是誰按的。
     * 放在審核單之後：有審核單的話，完成一定晚於審核通過，排在前面會讀成先完成才送審。
     */
    if (statusK === "done" && !autoDone) {
      out.push({
        type: "done",
        actor: "",
        text: `狀態變更為「已完成」${scoreText}`,
        time: fmtDate(completedAt || _.get(raw, "due_date")),
      });
    }
    /*
     * 進度回報。後端是 OrderByDescending(CreateAt)，reverse 之後就是由舊到新，
     * 跟時間軸上其他列同一個方向。
     *
     * 回報內容是 HTML（可含截圖）。這裡**刻意不把整段 HTML 塞進時間軸**：
     * 同一串 logs 裡還有「建立／指派／審核通過」等前端自己組句的列，那些必須維持
     * 純文字；而且時間軸是「一列一件事」，一張原尺寸截圖就會把整條軸撐開，
     * 十幾筆回報要捲半天才找得到下一件事。
     *
     * 取捨：**摘要維持一行純文字，圖另外挑出來當縮圖列在底下**（TaskReference 渲染，
     * 點縮圖走 RichView 既有的放大）。代價是回報內文的粗體／項目符號在這一格看不到 ——
     * 要看完整排版的人，個人工項頁與審核頁都是整段 RichView。
     * 換來的是：審核者在這裡就看得到交付物，而時間軸還是一條讀得完的時間軸。
     */
    records
      .slice()
      .reverse()
      .forEach((r, j, asc) => {
        const content = String(_.get(r, "content") || "");
        const shots = imagesOf(content);
        /*
         * 底下的縮圖列會畫出來（有畫得出來的圖，或有「圖片不能顯示」那段說明）時，
         * 摘要就不要再補一個「[圖片×1]」—— 圖都在下面了，標記只是雜訊。
         * 反過來，完全沒挑到圖（例如 <img> 的 src 是空的）才保留標記：
         * 那時畫面上沒有任何東西講得出「這則回報本來有一張圖」。
         */
        const shotsBlocked = Number(shots.blocked) || 0;
        const summary = toPlainText(content, {
          noImageMark: shots.count > 0 || shotsBlocked > 0,
        });

        /*
         * 「進度 40% → 50%」：這一格以前完全沒有進度值，看不出這次回報推進了多少。
         * 前一個值沒有欄位可以拿，但 asc 是由舊到新，往前找第一筆有值的就是上一次；
         * 找不到（最早那一筆）就只寫現在的值，不要編一個 0%。
         */
        const pctOf = (x) => {
          const v = _.get(x, "progress");
          const n = v == null || v === "" ? NaN : Number(v);
          return Number.isNaN(n) ? null : n;
        };
        const pv = pctOf(r);
        let prev = null;
        for (let k = j - 1; k >= 0; k -= 1) {
          const q = pctOf(asc[k]);
          if (q != null) {
            prev = q;
            break;
          }
        }
        const pctText = pv == null ? "" : `（${prev != null && prev !== pv ? `進度 ${prev}% → ${pv}%` : `進度 ${pv}%`}）`;

        out.push({
          type: "progress",
          actor: nameOf(r.create_id) || _.get(r, "create_id") || "",
          // 只有截圖、沒有文字的回報不能變成空白的一列：那時就只寫「回報進度」，圖在底下
          text: summary ? `回報進度：${summary}${pctText}` : `回報進度${pctText}`,
          time: fmtRelative(r.create_at),
          // 事後依 commit 補登的歷史記錄（不計入績效），要看得出來不是當時親手回報的
          backfill: !!_.get(r, "is_backfill", false),
          /** 回報**內容裡**的截圖（不是夾的檔案，那是下面的 progressFileOf） */
          shotsHtml: shots.html,
          shotsCount: shots.count,
          /** 網址不可信、只會畫成「圖片不能顯示」的那幾張：要數得出來才講得清楚 */
          shotsBlocked,
          // 回報時夾的檔案（有才帶）。以前這一步只留文字，檔名與回報 id 在這裡就被丟掉了，
          // 畫面上看得到「回報進度：…」卻沒有任何下載入口 —— 審核者拿不到交付物
          ...progressFileOf(r),
        });
      });
    return out;
  }, [raw, reviews, reassigns, records, statusK, assigneeName, score, nameOf]);

  /**
   * 回報時附的檔案，最新的在前面 —— 給「審核」那一段直接列出來。
   *
   * 「以前的紀錄」是依時間正排、預設收合，而且可能有十幾筆；審核者按「通過／要求修改」
   * 之前要先看到交付物，不能叫他自己去翻。後端雖然已經依時間倒排，這裡仍自己排一次，
   * 「最新的在最上面」是畫面上寫出來的承諾，不該默默依賴端點的排序。
   */
  const progressFiles = useMemo(
    () =>
      _.orderBy(
        records,
        [(r) => (r.create_at ? dayjs(r.create_at).valueOf() || 0 : 0), (r) => Number(r.id) || 0],
        ["desc", "desc"]
      )
        .map((r) => {
          const f = progressFileOf(r);
          return (
            f && {
              ...f,
              actor: nameOf(r.create_id) || _.get(r, "create_id") || "",
              time: fmtRelative(r.create_at),
            }
          );
        })
        .filter(Boolean),
    [records, nameOf]
  );

  /* ---------- 相依關係（GET /Task/Links 有，取不到就整區隱藏） ---------- */

  const deps = useMemo(() => {
    const pick = (arr) =>
      (arr || []).map((d) => ({
        code: d.code || d.issue_key || d.target_key || `#${d.id || ""}`,
        name: d.name || d.title || d.target_title || "",
        id: d.id || d.target_task_id,
      }));
    if (Array.isArray(links)) {
      const isBlockedBy = (d) =>
        ["blocked_by", "blockedBy", "is_blocked_by"].includes(String(d.type || d.link_type));
      return {
        blockedBy: pick(links.filter(isBlockedBy)),
        blocks: pick(links.filter((d) => !isBlockedBy(d))),
      };
    }
    return {
      blockedBy: pick(_.get(links, "blockedBy", _.get(links, "blocked_by", []))),
      blocks: pick(_.get(links, "blocks", [])),
    };
  }, [links]);

  /* ---------- 動作 ---------- */

  /**
   * 送出整份 ReqPutTask（後端是整筆覆蓋，缺欄位會被清掉，所以要先帶回原值）。
   *
   * 「原值」一定要送出前重讀一次，不能用畫面上那份 raw：審核人按下決定的那一刻，
   * 後端會替工項改狀態與進度（通過 → 已完成、要求修改 → 進行中並拉回 90%）。
   * 停在舊畫面上存一次說明或回報工時，就會把舊的「審核中 100%」整筆蓋回去 ——
   * 工項變成審核中卻沒有待審單，或已完成被打回審核中、少算一件完成。
   * 重讀失敗才退回手上那份。
   */
  const saveTask = useCallback(async (patch) => {
    const held = rawRef.current;
    if (!held) return;
    const fresh = await safe(
      () =>
        apiGet("/Task", {
          Data: jwtEncode({ TaskId: Number(held.id) }),
          id: held.id,
          taskId: held.id,
        }),
      null
    );
    const r = fresh && fresh.id != null ? { ...held, ...fresh } : held;
    const payload = {
      TaskId: r.id,
      PhaseId: r.phase_id ?? null,
      ParentTaskId: r.parent_task_id ?? null,
      Title: r.title,
      Description: r.description || "",
      // solution 欄位後端尚未建立；先一併送出，待後端補上即生效，目前會被忽略
      Solution: r.solution || "",
      Status: Number(r.status) || 0,
      Priority: Number(r.priority) || 0,
      Assignee: r.assignee || null,
      StartDate: r.start_date ? fmtInputDate(r.start_date) : null,
      DueDate: r.due_date ? fmtInputDate(r.due_date) : null,
      EstimatedHours: r.estimated_hours ?? null,
      ActualHours: r.actual_hours ?? null,
      Progress: Number(r.progress) || 0,
      Tags: r.tags || null,
      ...patch,
    };
    await apiPutForm("/Task", { Data: jwtEncode(payload), ...payload });
  }, []);

  /** 需求說明改好後存檔（以前是 onBlur 偷偷存，現在由「存起來」那一顆明確觸發） */
  const saveDesc = useCallback(
    async (next) => {
      const r = rawRef.current;
      if (!r || next === (r.description || "")) return;
      try {
        await saveTask({ Description: next });
        setRaw((s) => ({ ...s, description: next }));
        setDesc(next);
        toast("已儲存");
        if (onChanged) onChanged();
      } catch (e) {
        toast(errMsg(e, "儲存失敗"), "error");
      }
    },
    [saveTask, toast, onChanged]
  );

  const saveSol = useCallback(
    async (next) => {
      const r = rawRef.current;
      if (!r || next === (r.solution || "")) return;
      try {
        await saveTask({ Solution: next });
        setRaw((s) => ({ ...s, solution: next }));
        setSol(next);
        toast("已儲存");
        if (onChanged) onChanged();
      } catch (e) {
        toast(errMsg(e, "儲存失敗"), "error");
      }
    },
    [saveTask, toast, onChanged]
  );

  /**
   * 執行狀態轉換。
   *
   * resId 由呼叫端決定：需要解決結論的轉換（關閉之類）先讓使用者挑，
   * 挑完再進來。原本這裡固定送 null，於是「關閉」按下去必定收到
   * 「操作失敗: 需選擇解決結論」—— 按鈕是死路，沒有任何辦法真的關掉工項。
   */
  const doTransition = async (t, resId = null) => {
    if (t.locked || busy) return;
    setBusy(true);
    try {
      try {
        await apiPostJson("/Workflow/Transition", {
          taskId: Number(taskId),
          transitionId: t.transitionId || 0,
          toStatus: t.target == null ? null : STATUS_CODE[t.target],
          resolutionId: resId ?? null,
        });
      } catch (inner) {
        // 專案尚未套用工作流時 transitionId 不存在，退回直接改狀態
        if (t.transitionId) throw inner;
        await saveTask({ Status: STATUS_CODE[t.target] });
      }
      await reload();
      setPendingTrans(null);
      toast("狀態已變更");
      if (onChanged) onChanged();
    } catch (e) {
      toast(errMsg(e, "狀態變更失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 按下轉換鈕。需要解決結論的先展開挑選列，其餘直接執行。
   *
   * 結論預設選第一個（後端 GetResolutions 依 sort_order 排，第一個就是
   * 「已完成」這種常用值），所以正常情況只是多按一次「確認」。
   * 不直接替使用者填預設值：不予修復、重複這些結論會進報表，
   * 系統替他決定等於把資料弄髒。
   */
  const onClickTransition = (t) => {
    if (t.locked || busy) return;
    if (t.needsResolution) {
      setPendingTrans(t);
      setPendingResId(String(_.get(resolutions, [0, "id"], "")));
      return;
    }
    doTransition(t, null);
  };

  const toggleWatch = async () => {
    const next = !watch.watching;
    setWatch((s) => ({ ...s, watching: next })); // 樂觀更新，關注是低風險操作
    try {
      await apiPostJson("/Notification/Watch", {
        entityType: "task",
        entityId: Number(taskId),
        watch: next,
        watching: next,
      });
      const wt = await safe(
        () => apiGet("/Notification/Watch", { entityType: "task", entityId: taskId }),
        null
      );
      if (wt) {
        setWatch({ watching: !!wt.watching, watchers: wt.watchers || [] });
      }
    } catch (e) {
      setWatch((s) => ({ ...s, watching: !next }));
      toast(errMsg(e, "操作失敗"), "error");
    }
  };

  const handleDelete = () => {
    confirmDelete({
      title: "確定刪除此工項？",
      subtitle: "刪除後無法復原",
      body: (
        <>
          確定刪除「<b>{title}</b>」？其驗收條件、審核與紀錄將一併刪除。
        </>
      ),
      onConfirm: async () => {
        try {
          await apiDelete("/Task", {
            Data: jwtEncode({ TaskId: Number(taskId) }),
            id: taskId,
            taskId,
          });
          toast("已刪除");
          if (onChanged) onChanged();
          if (onBack) onBack();
        } catch (e) {
          toast(errMsg(e, "刪除失敗"), "error");
        }
      },
    });
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(raw);
      return;
    }
    // 沒有掛 onEdit 時，讓外層頁面自行決定要開哪個編輯畫面
    window.dispatchEvent(new CustomEvent("app:edit-task", { detail: { taskId, task: raw } }));
  };

  const openTask = (d) => {
    if (onOpenTask) {
      onOpenTask(d);
      return;
    }
    window.dispatchEvent(new CustomEvent("app:open-task", { detail: d }));
  };

  const toggleDod = async (c) => {
    const next = !c.is_checked;
    setCriteria((s) => s.map((x) => (x.id === c.id ? { ...x, is_checked: next } : x)));
    try {
      await apiPutJson("/Review/Criterion", { id: c.id, isDone: next, isChecked: next });
      const rv = await safe(() => apiGet("/Review/List", { taskId }), null);
      if (rv) setCriteria(_.get(rv, "criteria", []) || []);
    } catch (e) {
      setCriteria((s) => s.map((x) => (x.id === c.id ? { ...x, is_checked: !next } : x)));
      toast(errMsg(e, "更新失敗"), "error");
    }
  };

  const addDod = async () => {
    const t = dodDraft.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await apiPostJson("/Review/Criterion", { taskId: Number(taskId), content: t });
      setDodDraft("");
      const rv = await safe(() => apiGet("/Review/List", { taskId }), null);
      if (rv) setCriteria(_.get(rv, "criteria", []) || []);
      toast("已新增");
    } catch (e) {
      toast(errMsg(e, "新增失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 刪除驗收條件。後端 DELETE /Review/Criterion 早就有了（要 task.delete 權限），
   * 只是畫面上一直沒放按鈕 —— 打錯字或條件過時就只能一直掛著。
   *
   * 不跳確認對話框：一條完成條件就是一句話，重打的成本遠低於每次都要多按一次確認；
   * 這跟「刪工項」那種會連帶清掉留言與審核紀錄的操作不是同一個量級。
   */
  const removeDod = async (c) => {
    if (busy) return;
    setBusy(true);
    // 樂觀移除，失敗再放回去 —— 勾選那邊也是同一套做法
    setCriteria((s) => s.filter((x) => x.id !== c.id));
    try {
      await apiDelete("/Review/Criterion", { id: c.id });
      toast("已刪除");
    } catch (e) {
      const rv = await safe(() => apiGet("/Review/List", { taskId }), null);
      if (rv) setCriteria(_.get(rv, "criteria", []) || []);
      toast(errMsg(e, "刪除失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /* ---------- 附件 ---------- */

  const reloadAttachments = useCallback(async () => {
    const at = await safe(() => apiGet(`/Task/Attachment/List/${taskId}`), []);
    setAttachments(Array.isArray(at) ? at : []);
  }, [taskId]);

  /**
   * 上傳附件。後端 /Task/Attachment/{taskId} 一次只收一個檔（IFormFile file），
   * 所以多檔是在這裡逐一送出 —— 讓使用者一次選好幾個，不必開關檔案總管好幾趟。
   *
   * 逐檔回報成敗而不是整批失敗：十個檔案裡有一個太大，
   * 不該讓另外九個也白傳。
   */
  const uploadAttachments = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length || busy) return;
    setBusy(true);
    const failed = [];
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          // eslint-disable-next-line no-await-in-loop
          await apiPostForm(`/Task/Attachment/${taskId}`, fd);
        } catch (e) {
          failed.push(`${file.name}（${errMsg(e, "上傳失敗")}）`);
        }
      }
      await reloadAttachments();
      const okCount = list.length - failed.length;
      if (failed.length === 0) toast(`已上傳 ${okCount} 個檔案`);
      else if (okCount === 0) toast(`上傳失敗：${failed[0]}`, "error");
      else toast(`已上傳 ${okCount} 個檔案，${failed.length} 個失敗：${failed[0]}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const onPickAttachments = (e) => {
    const files = e.target.files;
    // 先清空 value，同一個檔案連續選兩次才會再觸發 change
    const picked = files ? Array.from(files) : [];
    e.target.value = "";
    uploadAttachments(picked);
  };

  /**
   * 下載附件 —— /Task/Attachment/Download/{id} 有 [Authorize]，
   * 直接開 <a href> 不會帶 Bearer token（必定 401），所以取 blob 再存檔。
   * 失敗訊息改走 downloadErrMsg：blob 模式下 errMsg 讀不到後端寫的原因。
   */
  const downloadAttachment = async (a) => {
    try {
      const blob = await request.get(`/Task/Attachment/Download/${a.id}`, null, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      saveBlob(blob, a.file_name || "download");
    } catch (e) {
      toast(await downloadErrMsg(e), "error");
    }
  };

  /**
   * 下載進度回報夾帶的檔案（GET /MyTask/Progress/Download/{回報 id}）。
   * 作法與 pages/MyTask/Index.js 的 downloadProgressFile 同一套：端點有 [Authorize]，取 blob 再存檔。
   * 審核者靠這一顆拿到交付物，失敗一定要講原因，不能按了沒反應。
   */
  const downloadProgressFile = async (f) => {
    try {
      const blob = await request.get(`/MyTask/Progress/Download/${f.recordId}`, null, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      saveBlob(blob, f.fileName || "attachment");
    } catch (e) {
      toast(await downloadErrMsg(e), "error");
    }
  };

  const removeAttachment = (a) =>
    confirmDelete({
      title: "確定刪除此附件？",
      subtitle: "刪除後無法復原",
      body: `確定刪除「${a.file_name}」？檔案將從伺服器移除。`,
      onConfirm: async () => {
        try {
          await apiDelete(`/Task/Attachment/${a.id}`);
          await reloadAttachments();
          toast("已刪除");
        } catch (e) {
          toast(errMsg(e, "刪除失敗"), "error");
        }
      },
    });

  /**
   * 子任務勾選。設計稿的 toggleSub 只改本地狀態，但那是靜態 mock；
   * 這裡列出的是真的子工項，只改本地會在下次載入被打回原形，
   * 所以樂觀更新後把子工項的狀態寫回 /Task（PUT 是整筆覆蓋，先 GET 回原值再只改 Status/Progress）。
   */
  const toggleSub = async (i) => {
    const cur = subs[i];
    if (!cur) return;
    const next = !cur.done;
    setSubs((s) => s.map((c, x) => (x === i ? { ...c, done: next } : c)));
    if (!cur.id) return; // 還沒寫進後端的暫存列，只留在本地
    try {
      const sub = await apiGet("/Task", {
        Data: jwtEncode({ TaskId: Number(cur.id) }),
        id: cur.id,
        taskId: cur.id,
      });
      if (!sub) throw new Error("找不到此小項目");
      const prev = Number(sub.status) || 0;
      const payload = {
        TaskId: sub.id,
        PhaseId: sub.phase_id ?? null,
        ParentTaskId: sub.parent_task_id ?? null,
        Title: sub.title,
        Description: sub.description || "",
        // 取消勾選時退回「進行中」（原本就是待處理則維持待處理）
        Status: next
          ? STATUS_CODE.done
          : prev === STATUS_CODE.done || prev === STATUS_CODE.closed
          ? STATUS_CODE.progress
          : prev,
        Priority: Number(sub.priority) || 0,
        Assignee: sub.assignee || null,
        StartDate: sub.start_date ? fmtInputDate(sub.start_date) : null,
        DueDate: sub.due_date ? fmtInputDate(sub.due_date) : null,
        EstimatedHours: sub.estimated_hours ?? null,
        ActualHours: sub.actual_hours ?? null,
        Progress: next ? 100 : Number(sub.progress) || 0,
        Tags: sub.tags || null,
      };
      await apiPutForm("/Task", { Data: jwtEncode(payload), ...payload });
      setSubs((s) => s.map((c, x) => (x === i ? { ...c, status: payload.Status } : c)));
      if (onChanged) onChanged();
    } catch (e) {
      setSubs((s) => s.map((c, x) => (x === i ? { ...c, done: !next } : c)));
      toast(errMsg(e, "更新失敗"), "error");
    }
  };

  const addSub = async () => {
    const t = subDraft.trim();
    if (!t) return;
    setSubs((s) => [...s, { id: null, text: t, status: 0, done: false }]);
    setSubDraft("");
    const pid = _.get(raw, "project_id");
    if (!pid) return;
    try {
      const payload = {
        ProjectId: pid,
        ParentTaskId: Number(taskId),
        /*
         * 明確指定子任務型別。後端 SetHierarchyOnSave 會驗「父的層級必須高於子」，
         * 而子任務是唯一層級 0 的型別，掛在任何型別底下都成立。
         * 以前不帶型別、後端一律套 task（層級 1），父也是 task 時 1 <= 1 就被擋下，
         * 加子項會安靜地失敗（例外被下面的 catch 吃掉，畫面上還留著那一列）。
         */
        IssueTypeKey: "sub_task",
        Title: t,
        Status: 0,
        Priority: Number(_.get(raw, "priority") || 1),
        Assignee: assigneeAccount || null,
      };
      await apiPostForm("/Task", { Data: jwtEncode(payload), ...payload });
      await reload();
      if (onChanged) onChanged();
    } catch (e) {
      // 失敗時把那一列收回去並說明原因 —— 留著一列其實不存在的小項目，
      // 使用者重新整理才發現不見了，比當下講清楚更糟
      setSubs((s) => s.filter((x) => !(x.id == null && x.text === t)));
      setSubDraft(t);
      toast(errMsg(e, "新增失敗"), "error");
    }
  };

  const toggleReviewer = (account) =>
    setPickedReviewers((s) => (s.includes(account) ? s.filter((x) => x !== account) : [...s, account]));

  const startReview = async () => {
    if (!pickedReviewers.length || busy) return;
    setBusy(true);
    try {
      await apiPostJson("/Review/Open", {
        taskId: Number(taskId),
        reviewType: REVIEW_TYPE_CODE[reviewType] ?? 0,
        reviewers: pickedReviewers,
        reviewerAccounts: pickedReviewers,
      });
      setPickedReviewers([]);
      setResubmitting(false);
      await reload();
      toast("已送出");
      if (onChanged) onChanged();
    } catch (e) {
      toast(errMsg(e, "送出失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (decision) => {
    if (!latestReview || busy) return;
    setBusy(true);
    try {
      await apiPostJson("/Review/Decide", {
        reviewId: latestReview.id,
        decision,
        comment: null,
      });
      /*
       * 通過 → 已完成、要求修改 → 進行中，都是後端 ReviewService.Decide 自己推的，
       * 不用（也不該）在這裡再補一發轉換。toast 依重讀回來的審核單與工項講結果，
       * 說法與審核頁共用 reviewOutcome —— 同一張審核單還有人沒表態、或自動轉換被守門擋下時，
       * 工項不會跟著變，不能只憑按的是哪一顆就說「完成了」。
       */
      const after = await reload();
      const mine = after.reviews.find((r) => String(r.id) === String(latestReview.id));
      const out = reviewOutcome(
        decision,
        mine ? reviewStatusKey(mine.status) : null,
        after.task && _.get(after.task, "status") != null ? statusKey(_.get(after.task, "status")) : null,
        after.task ? _.get(after.task, "progress", null) : null
      );
      toast(out.text, out.tone, out.ms);
      if (onChanged) onChanged();
    } catch (e) {
      toast(errMsg(e, "送出失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 刪除留言。後端 DELETE /Task/Comment 吃 ?Data=<JWT>（同 DELETE /Task 的慣例），
   * 權限是 task.delete —— 沒權限的人會拿到 403，訊息由 apiErrorMessage 講清楚。
   *
   * 不跳確認：一則留言重打的成本很低，而且旁邊就是紀錄，刪了看得出來。
   */
  const removeComment = async (cm) => {
    if (busy) return;
    setBusy(true);
    try {
      await apiDelete("/Task/Comment", { Data: jwtEncode({ CommentId: Number(cm.id) }) });
      setComments((s) => s.filter((x) => x.id !== cm.id));
      toast("已刪除");
    } catch (e) {
      toast(errMsg(e, "刪除失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    const t = commentDraft.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await apiPostForm("/Task/Comment", {
        Data: jwtEncode({ TaskId: Number(taskId), Content: t }),
        taskId,
        content: t,
      });
      setCommentDraft("");
      const cm = await safe(
        () => apiGet("/Task/Comment/List", { Data: jwtEncode({ TaskId: Number(taskId) }), taskId }),
        null
      );
      if (cm) setComments(Array.isArray(cm) ? cm : _.get(cm, "data_list", []) || []);
    } catch (e) {
      toast(errMsg(e, "留言失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /** 快速回報：組出一句話 → 寫進度歷程 →（有選狀態時）再推進狀態（設計稿 quickReport） */
  const quickReport = async () => {
    if (busy) return;
    const note = qrNote.trim();
    let txt = note;
    if (qrPct) txt += `${txt ? "，" : ""}進度 ${qrPct}%`;
    if (qrHours) txt += `${txt ? "，" : ""}登記 ${qrHours}h`;
    const stKey = LABEL_TO_STATUS[qrStatus];
    if (stKey) txt = `${txt ? `${txt}，` : ""}狀態→${qrStatus}`;
    if (!txt) return;

    // POST /MyTask/Progress 會把 task.progress 一起寫掉，之後的 PUT /Task 是整筆覆蓋，
    // 所以 PUT 一定要帶「剛寫進去的那個 progress」，否則會把進度蓋回舊值。
    const pctNum = Number(qrPct);
    const nextProgress =
      qrPct && !Number.isNaN(pctNum)
        ? Math.max(0, Math.min(100, Math.round(pctNum)))
        : Number(_.get(raw, "progress") || 0);

    let wfMatch = null;
    if (stKey && stKey !== statusK) {
      wfMatch =
        (_.get(wf, "transitions", []) || []).find(
          (t) => _.get(t, "to_status.legacy_status") === STATUS_CODE[stKey] && t.allowed
        ) || null;
    }
    /*
     * 需要結論的轉換（關閉之類）這一列沒有地方選，硬送必定收到「需選擇解決結論」。
     * 這個判斷一定要在寫進度歷程「之前」—— 否則歷程會多一筆假的「狀態→已關閉」，
     * 狀態其實沒變，而且按幾次就多幾筆。
     * 改成把「⋯ 更多」裡的結論選單展開，讓使用者在那裡選完再確認。
     */
    if (wfMatch && wfMatch.requires_resolution) {
      const t = transitions.find((x) => x.transitionId === wfMatch.id);
      if (t) setPendingTrans(t);
      toast(`「${qrStatus}」須選擇解決結論，請於上方「⋯ 更多」選擇後再確認`, "error");
      return;
    }

    setBusy(true);
    try {
      /*
       * 順序很重要：先 PUT（工時／退路狀態），最後才 POST 進度。
       *
       * POST /MyTask/Progress 在 progress >= 100 時會把狀態推到「審核中」並自動
       * 開一張審核單（MyTaskService.UpdateProgress → WorkflowService →
       * ReviewService.AutoOpenReview）。而 PUT /Task 是整筆覆蓋，帶的是畫面上
       * 那份還沒更新的 status —— 先 POST 再 PUT 的話，那一發 PUT 會把剛剛推好的
       * 「審核中」蓋回原本的狀態，審核單還開著、工項卻看起來沒送審。
       * 把 POST 放到最後，後端寫的狀態就是最終狀態。
       *
       * 另外「只填了進度」不需要 PUT：POST 自己就把 progress 寫進去了，
       * 多一發整筆覆蓋只是徒增把別的欄位寫回舊值的風險。
       */
      const patch = { Progress: nextProgress };
      let needPut = false;
      if (qrHours) {
        patch.ActualHours = Number(_.get(raw, "actual_hours") || 0) + Number(qrHours);
        needPut = true;
      }
      if (stKey && stKey !== statusK && !wfMatch) {
        // 沒有對應的工作流轉換時退回直接改欄位（工作流未設定的舊專案）
        patch.Status = STATUS_CODE[stKey];
        needPut = true;
      }
      if (needPut) await saveTask(patch);

      await apiPostForm("/MyTask/Progress", {
        taskId: Number(taskId),
        content: txt,
        progress: nextProgress,
      });
      // 轉換一定放在 PUT 之後：PUT 會用手上（尚未更新）的 status 整筆覆蓋，先轉換就會被蓋回去
      if (wfMatch) {
        await apiPostJson("/Workflow/Transition", {
          taskId: Number(taskId),
          transitionId: wfMatch.id,
          toStatus: STATUS_CODE[stKey],
          resolutionId: null,
        });
      }

      setQrNote("");
      setQrPct("");
      setQrHours("");
      setQrStatus(QR_STATUS_OPTS[0]);
      await reload();
      toast("回報已送出");
      if (onChanged) onChanged();
    } catch (e) {
      toast(errMsg(e, "送出失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 代負責人回報（設計稿 L730-742）。
   *
   * 走的是同一支 POST /MyTask/Progress。後端 MyTaskService.AddProgressRecord 會先用
   * ResolveReportableTask 判斷這次是不是代填（指派人本人以外的開單人／專案經理／
   * 工項資料範圍是全部的角色都放行），再由 MarkOnBehalf 自己在內容前面補上
   * 「（代 X 回報）」。所以這裡**不要**自己組「代 X 回報」的字串，
   * 否則畫面上會變成「（代 林怡君 回報）代 林怡君 回報：進度 80%」念兩次。
   *
   * 也不再補送 PUT /Task：AddProgressRecord 已經把 task.Progress 寫進去了，
   * 而 saveTask 是整筆覆蓋、帶的是手上那份還沒更新的 status ——
   * 回報 100% 時後端剛把狀態推到「審核中」並自動開了審核單，
   * 這一發 PUT 會把它蓋回「進行中」，等於把剛送出的審核吃掉。
   */
  const proxyReport = async () => {
    if (busy) return;
    const pctNum = Number(proxyPct);
    if (!proxyPct || Number.isNaN(pctNum)) {
      toast("請填寫數字，例如 80", "error");
      return;
    }
    const next = Math.max(0, Math.min(100, Math.round(pctNum)));
    setBusy(true);
    try {
      await apiPostForm("/MyTask/Progress", {
        taskId: Number(taskId),
        content: `進度 ${next}%`,
        progress: next,
      });
      setProxyPct("");
      await reload();
      toast("已完成代回報");
      if (onChanged) onChanged();
    } catch (e) {
      toast(errMsg(e, "送出失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /** 「回報進度」只是把人帶到工作區的回報欄位（整頁真正的主要動作在那裡） */
  const scrollToReport = () => {
    const el = document.getElementById("task-report-pct");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  const openFilePicker = () => {
    if (attFileRef.current) attFileRef.current.click();
  };

  /* ---------- 空 / 載入狀態（後端未回應也不能白畫面） ---------- */

  if (!raw) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        {loading ? (
          <PageLoading />
        ) : (
          <>
            <Typography variant="h2" component="p">
              無法開啟此工項
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              該工項可能已被刪除，或您沒有檢視權限。
            </Typography>
          </>
        )}
      </Box>
    );
  }

  /* ---------- 組裝：算出三個子元件要用的形狀 ---------- */

  const meAccount = currentAccount();

  /*
   * 可指定的審核者：預設排除指派人本人（自己驗自己等於沒驗），
   * 但如果他同時是這個專案的專案經理就放行 —— PM 本來就是驗收的人，
   * 一人專案或 PM 自己做掉一張工項時，不放行會讓那張工項永遠卡在審核中。
   * role 是 ProjectMemberRoleEnum，0 = 專案經理。
   */
  const selfIsManager = members.some(
    (m) => m.account === assigneeAccount && Number(m.role) === 0
  );
  const reviewerChips = selfIsManager
    ? members
    : members.filter((m) => m.account !== assigneeAccount);
  const bannerReviewers = (_.get(latestReview, "assignees", []) || [])
    .map((a) => nameOf(a.reviewer_id))
    .join("、");

  /* 唯讀模式：審核者要看的是「本次審核」，不一定是最新一筆，由呼叫端指定 reviewId */
  const metaReviewId = _.get(reviewMeta, "reviewId");
  const activeReview =
    (metaReviewId != null && reviews.find((r) => String(r.id) === String(metaReviewId))) ||
    latestReview;

  /*
   * 警語與確認視窗裡的「誰會看不到這件事」。
   * 只認真的有的名字：最新一次審核指定的把關者。拿不到就寫「審核人」——
   * 設計稿的人名全是範本假資料，照抄會變成畫面上出現一個不存在的同事。
   */
  const reviewerName = bannerReviewers || "審核人";

  /* 今天到期／逾期的判斷與「今天」頁同一套：due_date 存的是當天 00:00，
     直接比現在時刻的話，今天要交的工項早上八點就會被說成逾期。 */
  const today = dayjs().startOf("day");
  const dueDay = dueRaw && dayjs(dueRaw).isValid() ? dayjs(dueRaw).startOf("day") : null;
  const open = statusK !== "done" && statusK !== "closed";
  const dueToday = !!dueDay && dueDay.isSame(today, "day");
  const late = open && !!dueDay && dueDay.isBefore(today, "day");

  let tag = null;
  if (open && dueToday) {
    tag = {
      kind: "attention",
      mark: <MdWarningAmber />,
      text: `今日到期${progressPct < 100 ? `，尚差 ${100 - progressPct}%` : ""}`,
    };
  } else if (late) {
    const days = today.diff(dueDay, "day");
    tag = { kind: "attention", mark: <MdWarningAmber />, text: days > 0 ? `已逾期 ${days} 天` : "已逾期" };
  } else {
    const s = STATUS_TAG[statusK] || STATUS_TAG.pending;
    tag = { kind: s.kind, mark: s.mark, text: st.label };
  }

  const progressTone =
    statusK === "done"
      ? "done"
      : statusK === "closed"
      ? "closed"
      : tag.kind === "attention"
      ? "attention"
      : "";

  const dueText = dueDay ? `${dueToday ? "今天 " : ""}${fmtDay(dueRaw)}到期` : "";

  /* 需求說明的「誰什麼時候寫的」。後端沒有記「這個欄位是誰改的」，
     只有整筆工項的建立者與建立時間，所以寫得出來的就只有這一組；
     兩個都拿不到就整行不寫，不要編一個日期出來。 */
  const descAuthor = nameOf(_.get(raw, "create_id"));
  const descWhen = fmtDay(_.get(raw, "create_at"));
  const descMeta = descAuthor && descWhen ? `${descAuthor} 於 ${descWhen} 撰寫` : "";

  const typeLabel = typeField ? _.get(TYPE, [typeK, "label"], "") : "";

  /* 基本欄位：只放真的有值的，沒有的整格不出現（不要拿別的欄位冒充） */
  const facts = [];
  if (projName) facts.push({ label: "專案", value: projName });
  if (typeLabel) facts.push({ label: "型別", value: typeLabel });
  if (code) facts.push({ label: "代碼", value: code });
  if (assigneeName) facts.push({ label: "負責人", value: assigneeName });
  if (_.get(raw, "priority") != null) facts.push({ label: "優先級", value: PRIO_LABEL[prioK] });
  if (_.get(raw, "start_date")) facts.push({ label: "開始日期", value: fmtDay(_.get(raw, "start_date")) });
  if (dueRaw) facts.push({ label: "到期日", value: fmtDay(dueRaw) });
  if (est > 0) facts.push({ label: "預估工時", value: `${est} 小時` });
  if (act > 0) facts.push({ label: "實際工時", value: `${act} 小時` });
  facts.push({ label: "目前進度", value: `${progressPct}%` });
  if (score != null) facts.push({ label: "完成分數", value: String(score) });
  if (labels.length > 0) facts.push({ label: "標籤", value: labels.join("、") });
  if (prog) facts.push({ label: "小項目", value: `${prog.done}／${prog.total} 已完成（${prog.pct}%）` });

  /* 工時比較：沒有估時就沒有「估 vs 實」可言，畫一條以 20 小時為底的假進度條是騙人的 */
  const workload =
    est > 0
      ? { est, act, pct: Math.min(100, Math.round((act / est) * 100)), over: act > est }
      : null;

  const commentViews = comments
    .slice()
    .reverse()
    .map((cm) => ({
      id: cm.id,
      actor: cm.create_name || cm.create_id || "",
      time: fmtRelative(cm.create_at),
      html: mentionHtml(cm.content),
    }));

  const attachmentViews = attachments.map((a) => ({
    id: a.id,
    name: a.file_name,
    meta: [fmtSize(a.file_size), nameOf(a.create_id), fmtMD(a.create_at)].filter(Boolean).join(" · "),
    raw: a,
  }));

  const devLinkViews = devLinks.map((c, i) => ({
    key: c.id || `${c.kind || c.type || "commit"}-${i}`,
    kind: c.kind || c.type || "commit",
    label: c.label || c.title || c.message || c.url || "",
    ciText: ciText(c.ci ?? c.ci_status),
  }));

  const testViews = tests.map((t, i) => ({
    key: t.id || `${t.name}-${i}`,
    name: t.name || t.title,
    result: testText(t.result ?? t.status),
  }));

  const reviewVM = {
    status: reviewStatus,
    /*
     * 工項現在的狀態。審核通過會自動完成、要求修改會自動退回進行中，
     * 審核區的橫幅要照「工項真的變成什麼」講，而不是只看審核單 —— 見 TaskReference。
     */
    taskStatus: statusK,
    /** 工項現在的進度：退回只把超過 90% 的拉回 90%，橫幅要照實際數字講 */
    taskProgress: progressPct,
    /** 上一輪通過之後重新打開過（見 staleApproval），送審表單上要講為什麼又要送 */
    staleApproval,
    typeValue: reviewType,
    onChangeType: setReviewType,
    typeOptions: REVIEW_TYPE_BY_CODE,
    reviewerChips,
    picked: pickedReviewers,
    onToggleReviewer: toggleReviewer,
    onStartReview: startReview,
    onDecide: decide,
    onResubmit: () => {
      setResubmitting(true);
      setPickedReviewers([]);
    },
    bannerReviewers,
    latestTypeLabel: reviewTypeLabel(_.get(latestReview, "review_type")),
    selfIsManager,
    // 誰能按「通過／要求修改」由後端 /Review/Decide 把關；這裡沿用原本的顯示條件不變
    canDecide: true,
    /*
     * 我是不是這一輪還沒表態的把關者。只拿來決定要不要顯示「先下載來看再決定」那句提示 ——
     * canDecide 寫死成 true，送審的負責人自己也會被那句話叫去審自己送的東西。
     * 名單取不到時退回權責分立（送審的人不是把關者），與 pages/Approval/View.js 同一套。
     */
    isPendingReviewer: (() => {
      if (!activeReview || reviewStatusKey(activeReview.status) !== "pending") return false;
      const list = _.get(activeReview, "assignees", []) || [];
      if (list.length) {
        const mine = list.find((a) => String(a.reviewer_id) === String(meAccount));
        return !!mine && (Number(mine.decision) || 0) === 0;
      }
      return String(_.get(activeReview, "opened_by")) !== String(meAccount);
    })(),
    active: activeReview
      ? {
          typeLabel: reviewTypeLabel(_.get(activeReview, "review_type")),
          openerName: nameOf(_.get(activeReview, "opened_by")),
          openedAt: fmtDateTime(_.get(activeReview, "opened_at")),
          // SLA 由 /Approval 清單算好帶進來；/Review/List 沒有這個欄位，沒有就不寫
          sla: _.get(reviewMeta, "sla") || "",
          statusLabel: REVIEW_STATUS_LABEL[reviewStatusKey(_.get(activeReview, "status"))] || "待審",
          summary: _.get(activeReview, "summary_comment") || "",
          decisions: (_.get(activeReview, "assignees", []) || []).map((a) => ({
            name: nameOf(a.reviewer_id) || a.reviewer_id || "",
            label: DECISION_LABEL[Number(a.decision) || 0],
            at: a.decided_at ? fmtDateTime(a.decided_at) : "",
            comment: a.comment || "",
          })),
        }
      : null,
    rounds: reviews
      .filter((r) => !activeReview || String(r.id) !== String(activeReview.id))
      .map((r) => ({
        id: r.id,
        typeLabel: reviewTypeLabel(r.review_type),
        at: fmtDateTime(r.opened_at),
        statusLabel: REVIEW_STATUS_LABEL[reviewStatusKey(r.status)] || "待審",
      })),
  };

  /*
   * 代回報：要有 task 權限、自己是這個專案的專案經理、而且不是負責人本人。
   *
   * 後端是支援的：MyTaskService.ResolveReportableTask 放行指派人本人、開單的人、
   * 該專案的專案經理，以及工項資料範圍是「全部」的角色；同專案的其他一般成員仍然被擋。
   * 代填不會偽裝成本人 —— task_progress.create_id 與 activity_log.create_id 存的都是
   * 實際操作者，內容前面還會被 MarkOnBehalf 補上「（代 X 回報）」。
   *
   * 這裡故意只開給專案經理，跟設計稿那句「（只有專案經理看得到）」一致：
   * 後端另外放行的開單人／全範圍角色不會在這一區出現，但他們在「我的回報」那一格
   * 送出也會成功（見下面 ownerHint 的說明），功能沒有不見。
   */
  const meIsManager = members.some((m) => m.account === meAccount && Number(m.role) === 0);
  const meIsCreator = !!meAccount && String(_.get(raw, "create_id") || "") === meAccount;
  const notAssignee = !!assigneeAccount && !!meAccount && assigneeAccount !== meAccount;
  const canProxy = hasPermission("task") && meIsManager && notAssignee;

  /*
   * 不是負責人時，「我的回報」那一格要先講清楚送出去會怎樣。
   *
   * 後端 MyTaskService.ResolveReportableTask 放行「指派人本人／開單的人／該專案的
   * 專案經理／工項資料範圍是全部的角色」，其餘的人會拿到
   * 「這個工項不是指派給你的，只有指派人、開單人或該專案的專案經理可以回報進度」。
   * 專案經理與開單人前端判得出來，所以那兩種身分改講「會記成代他回報」；
   * 資料範圍是「全部」的角色前端讀不到（role claim 不在 token 裡，見 PermissionScope.Read），
   * 只能歸到保守的那一句 —— 寧可話說得保留，也不要保證一件送出後才發現不成立的事。
   */
  const ownerHint = !notAssignee
    ? ""
    : meIsManager || meIsCreator
    ? `此工項由${assigneeName}負責。您是${
        meIsManager ? "該專案的專案經理" : "此工項的建立者"
      }，可代為回報；送出後將記錄為「代${assigneeName}回報」並以您的名義登記，不會成為您的工項。`
    : `此工項由${assigneeName}負責，僅限負責人、建立者或該專案的專案經理回報進度；在此送出可能會被系統拒絕。如需變更狀態，請使用上方「⋯ 更多」；如需溝通，請於下方留言。`;

  /*
   * 審核模式：工項在審核中、而我是這一輪還沒表態的審核人。
   * 這時候我進來要做的是審核，不是回報 —— 先講「待您審核」，主要按鈕換成「開始審核」，
   * 回報表單不畫（說明、驗收條件、附件、活動紀錄照常顯示，審核時就是要看這些）。
   */
  const reviewFocus = !readOnly && statusK === "review" && reviewVM.isPendingReviewer;
  const goReview = () => navigate(`/Approvals/${taskId}`);

  return (
    /*
     * 整頁模式的內距由 Layout 的頁面容器給，這裡不能再包一層。
     * 內嵌（TaskDrawer）沒有那一層 —— 抽屜的內容區是貼邊的捲動容器，
     * 不補內距的話卡片會直接黏在抽屜邊框上。破例只有這一個值，走 --space-*。
     */
    <Box sx={embedded ? { p: "var(--space-4)" } : undefined}>
      {/* 附件的檔案挑選器：工作區與參考區的「＋ 加入附件」共用這一個 */}
      <input ref={attFileRef} type="file" multiple onChange={onPickAttachments} style={{ display: "none" }} />

      <TaskGlance
        embedded={embedded}
        readOnly={readOnly}
        busy={busy}
        typeLabel={typeLabel}
        tag={tag}
        title={title}
        assigneeName={assigneeName}
        dueText={dueText}
        projectName={projName}
        statusLabel={_.get(wf, "current.name") || st.label}
        pct={progressPct}
        progressTone={progressTone}
        reviewerName={reviewerName}
        onBack={embedded ? null : onBack}
        onReport={reviewFocus ? null : scrollToReport}
        onReview={reviewFocus ? goReview : null}
        transitions={transitions}
        onTransition={onClickTransition}
        onEdit={readOnly ? null : handleEdit}
        onDelete={readOnly ? null : handleDelete}
        pendingTrans={pendingTrans}
        resolutions={resolutions}
        pendingResId={pendingResId}
        onPickResolution={setPendingResId}
        onConfirmPending={() => doTransition(pendingTrans, Number(pendingResId))}
        onCancelPending={() => setPendingTrans(null)}
      />

      {reviewFocus ? (
        <Banner
          kind="warn"
          icon={<MdFactCheck />}
          title="此工項待您審核"
          sx={{ mt: 2 }}
          action={
            <Button variant="contained" onClick={goReview} sx={{ whiteSpace: "nowrap" }}>
              開始審核
            </Button>
          }
        >
          {[
            reviewVM.active && reviewVM.active.openerName ? `${reviewVM.active.openerName} 於 ${reviewVM.active.openedAt} 送審` : "負責人已送審",
            reviewVM.active && reviewVM.active.typeLabel ? `審核類型：${reviewVM.active.typeLabel}` : "",
          ]
            .filter(Boolean)
            .join("，")}
          。請檢視下方的工項說明、驗收條件與交付檔案後，於審核頁選擇「通過」或「要求修改」。
        </Banner>
      ) : null}

      <TaskWork
        hideReport={reviewFocus}
        readOnly={readOnly}
        busy={busy}
        descHtml={desc}
        descMeta={descMeta}
        onSaveDesc={readOnly ? null : saveDesc}
        reviewerName={reviewerName}
        ownerHint={ownerHint}
        qrPct={qrPct}
        /* 進度輸入是五顆大按鈕，空值要顯示「現在是幾 %」才知道自己在改什麼 */
        currentPct={Math.max(0, Math.min(100, Math.round(Number(_.get(raw, "progress")) || 0)))}
        onChangePct={setQrPct}
        qrNote={qrNote}
        onChangeNote={setQrNote}
        qrStatus={qrStatus}
        onChangeStatus={setQrStatus}
        statusOpts={QR_STATUS_OPTS}
        qrHours={qrHours}
        onChangeHours={setQrHours}
        onSubmit={quickReport}
        onAddAttachment={openFilePicker}
      />

      <TaskReference
        readOnly={readOnly}
        busy={busy}
        criteria={criteria}
        onToggleDod={toggleDod}
        onRemoveDod={removeDod}
        dodDraft={dodDraft}
        onChangeDodDraft={setDodDraft}
        onAddDod={addDod}
        logs={logs}
        comments={commentViews}
        commentDraft={commentDraft}
        onChangeCommentDraft={setCommentDraft}
        onAddComment={addComment}
        onRemoveComment={removeComment}
        watcherNames={watch.watchers.map((w) => nameOf(w))}
        watching={watch.watching}
        onToggleWatch={toggleWatch}
        canProxy={canProxy}
        assigneeName={assigneeName}
        proxyPct={proxyPct}
        currentPct={Math.max(0, Math.min(100, Math.round(Number(_.get(raw, "progress")) || 0)))}
        onChangeProxyPct={setProxyPct}
        onProxyReport={proxyReport}
        facts={facts}
        solHtml={sol}
        onSaveSol={readOnly ? null : saveSol}
        subs={subs}
        subDraft={subDraft}
        onChangeSubDraft={setSubDraft}
        onAddSub={addSub}
        onToggleSub={toggleSub}
        deps={deps}
        onOpenTask={openTask}
        workload={workload}
        attachments={attachmentViews}
        onDownloadAttachment={downloadAttachment}
        onRemoveAttachment={removeAttachment}
        progressFiles={progressFiles}
        progressUnavailable={recordsFailed}
        onDownloadProgressFile={downloadProgressFile}
        review={reviewVM}
        devLinks={devLinkViews}
        tests={testViews}
        onAddAttachment={openFilePicker}
        onLinkCode={() =>
          // /Task/DevLinks 後端還沒做，這裡不假裝有；講清楚比給一個沒反應的按鈕好
          /* 讀得到但建不了：TaskController 只有 GET /Task/DevLinks，沒有任何 POST */
          toast("目前僅能檢視已關聯的程式碼，尚未提供新增功能。", "error")
        }
        code={code}
        updatedText={fmtDay(_.get(raw, "update_at"))}
      />
    </Box>
  );
}

TaskDetailPanel.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectName: PropTypes.string,
  onBack: PropTypes.func,
  onChanged: PropTypes.func,
  /** 未提供時改發 app:edit-task 事件，讓外層頁面決定開哪個編輯畫面 */
  onEdit: PropTypes.func,
  /** 相依關係被點擊時開啟另一個工項；未提供時改發 app:open-task 事件 */
  onOpenTask: PropTypes.func,
  /** 唯讀：隱藏編輯 / 刪除 / 狀態轉換 / 回報等所有會改資料的動作，只留資訊呈現 */
  readOnly: PropTypes.bool,
  /** 內嵌：外框與標題列由呼叫端（例如抽屜）負責，元件不畫標題也不改全域導覽狀態 */
  embedded: PropTypes.bool,
  /** 唯讀模式下要凸顯的那一次審核；sla / slaColor 由簽核清單算好帶入（/Review/List 沒有這兩個欄位） */
  reviewMeta: PropTypes.shape({
    reviewId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sla: PropTypes.string,
    slaColor: PropTypes.string,
  }),
  /** 工項代碼（例如 MOB-135）；/Task 沒有回 issue_key 時用它顯示，避免退化成 #id */
  taskCode: PropTypes.string,
};

TaskDetailPanel.defaultProps = {
  taskId: null,
  projectName: "",
  onBack: () => {},
  onChanged: () => {},
  onEdit: null,
  onOpenTask: null,
  readOnly: false,
  embedded: false,
  reviewMeta: null,
  taskCode: "",
};

export default TaskDetailPanel;
